import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import {
  organizationsRolesListOptions,
  organizationsUserGroupsApplicationsRolesPartialUpdateMutation
} from 'src/api/main'
import type { UserGroup, UserGroupRoleShort } from 'src/api/main'
import { invalidateWorkspaceUserGroups } from 'src/features/users/hooks'

const PAGE_SIZE = 200

export const useAppRoleOptions = (
  organizationId: number,
  applicationId: number,
  enabled = true
) => {
  const { data, isLoading } = useQuery({
    ...organizationsRolesListOptions({
      query: {
        organization: organizationId,
        application: applicationId,
        per_page: PAGE_SIZE
      }
    }),
    enabled:
      enabled &&
      Number.isFinite(organizationId) &&
      Number.isFinite(applicationId)
  })

  const roleOptions = useMemo(
    () =>
      (data?.results ?? []).map((role) => ({
        value: role.id,
        label: role.name
      })),
    [data]
  )

  return { roleOptions, isLoading }
}

/** Split a group's roles into this application's and everyone else's. */
export const partitionRolesByApp = (
  group: UserGroup,
  applicationId: number
): { own: UserGroupRoleShort[]; foreign: UserGroupRoleShort[] } => {
  const own: UserGroupRoleShort[] = []
  const foreign: UserGroupRoleShort[] = []

  ;(group.roles ?? []).forEach((role) => {
    if (role.application === applicationId) own.push(role)
    else foreign.push(role)
  })

  return { own, foreign }
}

/**
 * Replace the roles a group grants **within one application**.
 *
 * This is the only safe write from inside an app: the screen shows just this
 * app's roles, so PATCHing the group-level `roles` array would send a set that
 * is complete from the admin's view but missing every other app's roles — and
 * Core replaces that array wholesale. The dedicated endpoint scopes the
 * replacement so foreign roles are left alone.
 */
export const useAppGroupRoleMutation = (
  organizationId: number,
  applicationId: number
) => {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation(
    organizationsUserGroupsApplicationsRolesPartialUpdateMutation()
  )

  const setGroupRoles = useCallback(
    async (groupId: number, roleIds: number[]) => {
      await mutateAsync({
        // The schema types this path segment as a string, not a number.
        path: { application_id: String(applicationId), id: groupId },
        body: { roles: roleIds }
      })
      await invalidateWorkspaceUserGroups(queryClient, organizationId)
    },
    [applicationId, mutateAsync, organizationId, queryClient]
  )

  return { setGroupRoles, isPending }
}
